import { type Permission } from '@/shared/DriveTypes';
import cx from 'classnames';
import React from 'react';
import { match } from 'ts-pattern';
import * as buttonStyles from '~/elements/Buttons/Button.css';
import { focusInput, onKeyDown } from '~/utils/utils';
import * as styles from './FileModal.css';

export type PageModalType = 'document' | 'sheet' | 'slide' | 'form' | 'folder';
export type ModalType = 'rename' | 'trash' | 'copy' | 'link' | 'shortcut' | 'move' | PageModalType;

type BaseProps = {
  onCancel: (e: React.MouseEvent<HTMLButtonElement | HTMLOrSVGElement>) => void;
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  defaultValue?: string | undefined;
  defaultURLValue?: string | undefined;
  onChangeURL?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  title: string;
  description?: string | React.ReactNode;
  permissions?: Permission[] | undefined;
  style?: React.CSSProperties;
};

type Props = BaseProps &
  (
    | {
        modalType: 'link';
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
        onChangeURL: (e: React.ChangeEvent<HTMLInputElement>) => void;
        defaultValue?: string | undefined;
        defaultURLValue?: string | undefined;
      }
    | {
        modalType: PageModalType;
        defaultValue: string | undefined;
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
      }
    | {
        modalType: 'rename';
        defaultValue: string | undefined;
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
      }
    | {
        modalType: 'copy' | 'trash';
        description: string | React.ReactNode;
      }
    | {
        modalType: 'share';
        permissions: Permission[] | undefined;
      }
  );

export const Title = ({ children, className, ...props }: React.HTMLAttributes<HTMLHeadingElement>) => (
  <h2 className={cx(styles.modalTitle, className)} id="modal-title" {...props}>
    {children}
  </h2>
);

const ButtonCancel = ({
  onClick,
}: {
  onClick: (e: React.MouseEvent<HTMLButtonElement | HTMLOrSVGElement>) => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      marginRight: 6,
    }}
    className={buttonStyles.transparent}
  >
    Cancel
  </button>
);

const ButtonSubmit = (props: React.ButtonHTMLAttributes<HTMLButtonElement> & { children: React.ReactNode }) => (
  <button type="submit" {...props}>
    {props.children}
  </button>
);

export const FileModal = ({
  title,
  modalType,
  onCancel,
  onSubmit,
  onChange,
  defaultValue,
  defaultURLValue,
  onChangeURL,
  description,
  style,
}: Props) => {
  const submitText = match(modalType)
    .with('rename', () => 'Save')
    .with('copy', () => 'Make a copy')
    .with('trash', () => 'Move to trash')
    .otherwise(() => 'Create');

  if (modalType === 'link') {
    return (
      <form className={styles.modal} style={style} onSubmit={onSubmit} onKeyDown={onKeyDown} aria-label="form">
        <Title>{title}</Title>
        <label id="link-name" htmlFor="link-name" style={{ display: 'block', marginBottom: '.5rem' }}>
          Name <small>(optional)</small>
        </label>
        <input
          aria-labelledby="link-name"
          ref={focusInput}
          onChange={(e) => {
            onChange(e);
          }}
          className={styles.modalInput}
          defaultValue={defaultValue}
        />
        <br />
        <br />
        <label id="link-url" htmlFor="link-url" style={{ display: 'block', marginBottom: '.5rem' }}>
          Web address
        </label>
        <input
          aria-labelledby="link-url"
          placeholder="example.com"
          onChange={(e) => onChangeURL(e)}
          className={styles.modalInput}
          required
          pattern=".+\..+"
          defaultValue={defaultURLValue}
        />
        <div
          style={{
            paddingTop: 34,
            textAlign: 'right',
          }}
        >
          <ButtonCancel onClick={onCancel} />
          <ButtonSubmit className={buttonStyles.primary}>{submitText}</ButtonSubmit>
        </div>
      </form>
    );
  }

  return (
    <form className={styles.modal} style={style} onSubmit={onSubmit} onKeyDown={onKeyDown} aria-label="form">
      <Title>{title}</Title>
      {onChange && (
        <input
          aria-labelledby="modal-title"
          ref={focusInput}
          defaultValue={defaultValue}
          onChange={onChange}
          required
          className={styles.modalInput}
        />
      )}
      {/* Allows closing the modal via the keyboard */}
      {!onChange && (
        <input
          aria-labelledby="modal-title"
          ref={focusInput}
          style={{ opacity: 0, position: 'absolute', height: 0, width: 0 }}
        />
      )}
      {description ? <p>{description}</p> : null}
      <div
        style={{
          paddingTop: 34,
          textAlign: 'right',
        }}
      >
        <ButtonCancel onClick={onCancel} />
        <ButtonSubmit className={buttonStyles.primary}>{submitText}</ButtonSubmit>
      </div>
    </form>
  );
};
